import { sessionFetch, SessionEndedError } from "@/lib/session";
import { type ClassValue, clsx } from "clsx";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { PDFDocument } from "pdf-lib";
import { AuthError, APIError, NetworkError, readableErrorMessage } from "./errors";
import { apiBase, resolveApiUrl } from "./apiBase";

// Cached Intl number formatters — replace the heavier `numeral` dependency in
// these app-wide helpers so light screens don't pull it into the entry bundle.
const usdInteger = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const usdDecimal = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
// `numeral("0.[00]")` — up to 2 decimals, no grouping, no trailing zeros.
const compactDecimal = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
  useGrouping: false,
});
// `numeral("0.[000000]")` — up to 6 decimals, no grouping.
const preciseDecimal = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 6,
  useGrouping: false,
});
// `numeral("0,0.[000000]")` — up to 6 decimals, with thousands grouping.
const preciseGroupedDecimal = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 6,
});
// `numeral("0.[00]%")` — value is a fraction; up to 2 decimals, no grouping.
const percentTwoDp = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumFractionDigits: 2,
  useGrouping: false,
});

dayjs.extend(utc);
dayjs.extend(relativeTime);

dayjs.extend(utc);
dayjs.extend(relativeTime);

function throwFetchError(error: unknown): never {
  if (error instanceof SessionEndedError) throw new AuthError(error.message);
  // If it's already one of our custom errors, re-throw it as-is
  if (error instanceof AuthError || error instanceof APIError || error instanceof NetworkError) {
    throw error;
  }

  // Handle network errors (fetch failed)
  if (error instanceof TypeError && error.message.includes("fetch")) {
    const networkError = new NetworkError("Network request failed. Please check your connection.");
    console.error("Network error:", networkError);
    throw networkError;
  }

  // For unexpected errors, log them and re-throw
  console.error("Unexpected fetch error:", error);
  throw error;
}

async function readApiError(response: Response): Promise<string> {
  const data = await response.json().catch(() => ({
    error: `${response.status} ${response.statusText || "Request failed"}`,
  }));
  return readableErrorMessage(data?.message || data?.error || "Something went wrong");
}

/**
 * Client-side fetcher utility
 *
 * NOTE: This is for client-side use only. For server-side fetching, use serverFetcher from @/lib/server/fetcher
 *
 * Security Notes:
 * - Tokens are now stored in httpOnly cookies (XSS protection)
 * - The API proxy automatically reads tokens from cookies
 * - We still check localStorage for backward compatibility
 * - credentials: "include" ensures cookies are sent with requests
 *
 * Error Handling:
 * - Throws AuthError only when session recovery confirms the session has ended
 * - Throws APIError for other HTTP errors
 * - Throws NetworkError for network failures
 *
 * @param url - API endpoint path
 * @param method - HTTP method
 * @param body - Request body
 * @param headers - Additional headers
 * @returns Promise with API response data
 */
export async function fetcher(
  url: string,
  method: string = "GET",
  body?: any,
  headers?: Record<string, string>,
) {
  const role = localStorage.getItem("role");

  const isFormData = body instanceof FormData;

  try {
    const queryString = method === "GET" && body ? new URLSearchParams(body).toString() : "";
    if (queryString) url += `?${queryString}`;

    let requestBody: BodyInit | undefined;
    if (method !== "GET") requestBody = isFormData ? body : JSON.stringify(body);

    const response = await sessionFetch(resolveApiUrl(url), {
      method,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(!isFormData && method !== "GET" ? { "Content-Type": "application/json" } : {}),
        ...headers,
        ...(role ? { "X-Role": role } : {}),
      },
      body: requestBody,
    });

    if (!response.ok) {
      const errorMessage = await readApiError(response);

      throw new APIError(errorMessage, response.status);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throwFetchError(error);
  }
}

/** Extract list rows from paginated API responses (`items`, nested `data.items`, etc.). */
export function extractPaginatedItems<T = Record<string, unknown>>(payload: unknown): T[] {
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload)) return payload as T[];

  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.items)) return record.items as T[];
  if (Array.isArray(record.data)) return record.data as T[];
  if (Array.isArray(record.rows)) return record.rows as T[];

  const nested = record.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return extractPaginatedItems<T>(nested);
  }

  return [];
}

export async function fetchAllPages<T>(url: string, filters: Record<string, any>): Promise<T[]> {
  let page = 1;
  const limit = 100;
  let allItems: T[] = [];

  while (true) {
    const queryParams = new URLSearchParams();
    Object.entries({ ...filters, page, limit }).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          queryParams.append(key, value.join(","));
        } else {
          queryParams.append(key, value.toString());
        }
      }
    });
    const response = await fetcher(`${url}?${queryParams.toString()}`);
    const items = response?.items || response?.data || response?.rows || [];
    allItems.push(...items);
    if (!items.length || items.length < limit) break;
    page++;
  }

  return allItems;
}

export async function directToBEFetcher<T = unknown>(
  url: string,
  method: string = "GET",
  body?: any,
  headers?: Record<string, string>,
): Promise<T> {
  const isFormData = body instanceof FormData;

  const requestHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };
  if (!isFormData && body && typeof body === "object") {
    requestHeaders["Content-Type"] = "application/json";
  }

  // Cookie-auth model: the browser sends the httpOnly auth cookie automatically
  // (the old `/api/auth/token` Bearer endpoint was removed in the SPA migration).
  // resolveApiUrl maps legacy `/api/*` paths to `apiBase()` + `/v1/*`.
  const response = await sessionFetch(resolveApiUrl(url), {
    method,
    credentials: "include",
    headers: requestHeaders,
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let errorMessage = response.statusText || "Request failed";
    try {
      const err = (await response.json()) as {
        error?: string;
        message?: string;
      };
      errorMessage = err?.error ?? err?.message ?? errorMessage;
    } catch {
      // ignore non-JSON error body
    }
    throw new Error(readableErrorMessage(errorMessage));
  }

  const contentType = response.headers.get("content-type");
  const contentLength = response.headers.get("content-length");
  if (contentLength === "0") {
    return {} as T;
  }
  if (!contentType?.includes("application/json")) {
    try {
      const text = await response.text();
      return (text ? { message: text } : {}) as T;
    } catch {
      return {} as T;
    }
  }
  const text = await response.text();
  if (!text) return {} as T;
  return JSON.parse(text) as T;
}

export async function forceFetcher(url: string) {
  // Only for GET requests
  sessionStorage.removeItem(url);
  return fetcher(url);
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const escapeCSVValue = (value: any): string => {
  if (value === null || value === undefined) return "";
  let stringValue = String(value);
  // Neutralize CSV formula injection: a leading =, +, -, @, tab, or CR can be
  // executed as a formula by spreadsheet apps. Prefix with a single quote so
  // the value is treated as text. See OWASP "CSV Injection".
  if (/^[=+\-@\t\r]/.test(stringValue)) {
    stringValue = `'${stringValue}`;
  }
  if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const exportToCSV = (
  data: any[],
  fileName: string,
  headers: { label: string; key: string }[],
) => {
  try {
    if (data?.length === 0) return;
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [
        headers.map((header) => escapeCSVValue(header.label)).join(","),
        ...data?.map((row) =>
          headers.map((header) => escapeCSVValue(row[header.key] ?? "")).join(","),
        ),
      ].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error exporting CSV:", error);
  }
};

export const getCurrentDate = () => {
  const today: Date = new Date();
  const year: number = today.getFullYear();
  let month: number | string = today.getMonth() + 1;
  let day: number | string = today.getDate();
  if (month < 10) {
    month = `0${month}`;
  }
  if (day < 10) {
    day = `0${day}`;
  }
  return `${year}-${month}-${day}`;
};

export function trimString(str: string, limit = 22) {
  if (!str) return;
  return str.length <= limit ? str : `${str.slice(0, limit)}...`;
}

export function generateCode() {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const letter1 = letters.charAt(Math.floor(Math.random() * letters.length));
  const letter2 = letters.charAt(Math.floor(Math.random() * letters.length));
  const numbers = Math.floor(Math.random() * 10000)
    .toString()
    .padStart(4, "0");
  const code = `${letter1}${letter2}-` + numbers;
  return code;
}

export const capitalizeFirstLetter = (string: string) => {
  return string?.charAt(0)?.toUpperCase() + string?.slice(1);
};

// Character classes for the password generator — exclude ambiguous chars
// (0 O o 1 l I) for readability. These are alphabets, not secrets.
const CHARSET_UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const CHARSET_LOWER = "abcdefghijkmnpqrstuvwxyz";
const CHARSET_DIGITS = "23456789";
const CHARSET_SYMBOLS = "!@#$%^&*-_=+?";
const CHARSET_ALL = CHARSET_UPPER + CHARSET_LOWER + CHARSET_DIGITS + CHARSET_SYMBOLS;

// Cryptographically random index into a charset, with rejection sampling to avoid modulo bias
function randomIndex(max: number): number {
  const limit = Math.floor(0xffffffff / max) * max;
  const buf = new Uint32Array(1);
  let value: number;
  do {
    globalThis.crypto.getRandomValues(buf);
    value = buf[0];
  } while (value >= limit);
  return value % max;
}

const pickChar = (charset: string): string => charset[randomIndex(charset.length)];

/**
 * Generate a readable, cryptographically-random password guaranteeing at least
 * one upper/lower/digit/symbol. Used for the optional data-room password.
 */
export function generateReadablePassword(length = 20): string {
  const chars = [
    pickChar(CHARSET_UPPER),
    pickChar(CHARSET_LOWER),
    pickChar(CHARSET_DIGITS),
    pickChar(CHARSET_SYMBOLS),
  ];
  while (chars.length < length) {
    chars.push(pickChar(CHARSET_ALL));
  }

  // Fisher–Yates shuffle so the guaranteed chars aren't always at the front
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

/**
 * True when `url` targets our own API — a root-relative path, the configured API
 * origin, or the current page origin — and may therefore carry the auth cookie
 * and `X-Role` header. Pre-signed storage URLs (S3 / Supabase), third-party
 * hosts, and blob/data URLs return false: they authenticate by signature (or not
 * at all), so sending credentials risks CORS failures and leaks `X-Role`
 * off-origin.
 */
export function isApiFileUrl(url: string): boolean {
  if (url.startsWith("blob:") || url.startsWith("data:")) {
    return false;
  }
  if (url.startsWith("/") && !url.startsWith("//")) {
    return true;
  }
  const base = apiBase();
  if (base && (url === base || url.startsWith(`${base}/`))) {
    return true;
  }
  if (globalThis.window !== undefined) {
    const { origin } = globalThis.location;
    if (url === origin || url.startsWith(`${origin}/`)) {
      return true;
    }
  }
  return false;
}

/**
 * Build `fetch` init for a file/document URL. API-served files are cookie-authed
 * and now cross-origin, so they need `credentials: "include"` (+ the `X-Role`
 * header); everything else (pre-signed storage, third-party, blob) is fetched
 * WITHOUT credentials or internal headers. Passing every file fetch through here
 * keeps that distinction in one place — and stays correct when list endpoints
 * switch from pre-signed URLs to on-demand API URLs.
 */
export function fileFetchInit(url: string, init: RequestInit = {}): RequestInit {
  if (!isApiFileUrl(url)) {
    return { ...init, mode: "cors", credentials: "omit" };
  }
  const role = typeof localStorage === "undefined" ? null : localStorage.getItem("role");
  const headers = new Headers(init.headers);
  if (role) {
    headers.set("X-Role", role);
  }
  return { ...init, credentials: "include", headers };
}

/**
 * Fetch a file/document URL correctly regardless of its origin. Resolves
 * relative API paths (e.g. `/v1/files/:id/content`) to the API origin, attaches
 * credentials for API URLs, and leaves pre-signed storage URLs uncredentialed.
 * Use this for every file/document fetch so list endpoints can return on-demand
 * API paths instead of pre-signed storage URLs.
 */
export async function fileFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const resolved = resolveApiUrl(url);
  return sessionFetch(resolved, fileFetchInit(resolved, init));
}

export const downloadFile = async (url: string, fileName: string) => {
  try {
    const response = await fileFetch(url);
    if (!response.ok) {
      throw new Error("Failed to fetch the file");
    }
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName || "document.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Failed to download the document.", error);
  }
};

export const isDataCompleted = (schema: any, data: any, tab: string) => {
  const condition = schema[tab as keyof typeof schema];
  if (!condition) return false;
  if (condition?.alwaysTrue) return true;
  const hasAllFields = condition?.fields?.every((field: any) => Boolean(data?.[field]));
  return condition.validator ? hasAllFields && condition.validator(data) : hasAllFields;
};

const compactCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** Compact currency, e.g. $1.2K / $3.4M. */
export const formatCompactCurrency = (amount: number) => compactCurrencyFormatter.format(amount);

export const getDollars = (props: any) => {
  if (!props) return "$0";
  const { amount } = props;
  if (!amount) return "$0";
  const parsedAmount = typeof amount === "number" ? amount : parseFloat(amount);

  if (isNaN(parsedAmount)) {
    console.error("Invalid amount. Please provide a valid number.");
    return;
  }
  const hasDecimal = parsedAmount % 1 !== 0;
  return (hasDecimal ? usdDecimal : usdInteger).format(parsedAmount);
};

export function toNumber(value: string | number | undefined) {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  return parseFloat(value);
}

export function normalizeE164(text?: string) {
  if (!text) return "";
  return text?.replace(/\s+/g, "");
}

export function throttle(fn: (...args: any[]) => void, wait: number) {
  let lastTime = 0;
  return function (...args: any[]) {
    const now = new Date().getTime();
    if (now - lastTime >= wait) {
      fn(...args);
      lastTime = now;
    }
  };
}

export const formatDate = (dateString: string | null) => {
  if (!dateString) return "N/A";
  return dayjs(dateString).format("MMM D, YYYY h:mm A");
};

export const formatDateOnly = (dateString: string | null) => {
  if (!dateString) return "N/A";
  // Invoice/calendar dates arrive as date-only UTC midnight (e.g. "2026-06-12T00:00:00+00:00").
  // Format in UTC so they don't shift back a day for viewers west of UTC.
  return dayjs.utc(dateString).format("MMM D, YYYY");
};

/**
 * Same idea as `formatDateOnly` but with time: shows the timestamp as stored
 * (UTC), not converted to the viewer's browser timezone. Use for source-of-truth
 * timestamps (e.g. an email's actual send time) where "the real value" matters
 * more than localizing it — `formatDate` still converts to local time and is the
 * right choice for most in-app activity timestamps.
 */
export const formatDateTimeUtc = (dateString: string | null) => {
  if (!dateString) return "N/A";
  return dayjs.utc(dateString).format("MMM D, YYYY h:mm A");
};

export const formatChartMonthLabel = (dateString: string, spansMultipleYears: boolean) => {
  const date = dayjs(dateString);
  return spansMultipleYears ? date.format("MMM YY") : date.format("MMM");
};

export const formatMonthYear = (dateString: string | null) => {
  if (!dateString) {
    return "N/A";
  }
  return dayjs.utc(dateString).format("MMM YYYY");
};

export const formatAmount = (
  amount: number | null | undefined,
  view = "long",
  isCurrency = true,
) => {
  if (!amount && amount !== 0) return "NA";

  const symbol = isCurrency ? "$" : "";

  let formattedAmount = "";

  switch (true) {
    case amount >= 1e12:
      formattedAmount = `${compactDecimal.format(amount / 1e12)}tn`;
      break;
    case amount >= 1e9:
      formattedAmount = `${compactDecimal.format(amount / 1e9)}bn`;
      break;
    case amount >= 1e6:
      formattedAmount = `${compactDecimal.format(amount / 1e6)}m`;
      break;
    case amount >= 1e3:
      formattedAmount = `${compactDecimal.format(amount / 1e3)}k`;
      break;
    default:
      formattedAmount = preciseDecimal.format(amount);
  }

  return view === "short"
    ? `${symbol}${formattedAmount}`
    : `${symbol}${preciseGroupedDecimal.format(amount)}`;
};

/** `value` rounded down to `decimals` places, so it never lands above a ceiling. */
export function floorToDecimals(value: number, decimals: number): number {
  const scale = 10 ** decimals;
  // toFixed first: 8.2 * 100 is 819.9999…, which a bare floor turns into 8.19.
  return Math.floor(Number((value * scale).toFixed(6))) / scale;
}

/** Round to nearest dollar before formatting. Returns "-" for null/undefined. */
export const formatDollars = (v: number | null | undefined) =>
  v != null ? formatAmount(Math.round(v)) : "-";

export const formatAddress = (address: any) => {
  if (!address) return "-";
  const { address_line_1, address_line_2, city, region, postal_code } = address;
  const formatted = [address_line_1, address_line_2, city, region, postal_code]
    .filter(Boolean)
    .join(", ");
  return formatted || "-";
};

export const cleanMemoHtml = (memo?: string): string => {
  if (!memo) return "";
  return memo
    .replaceAll(/<span class="ql-ui".*?<\/span>/g, "")
    .replaceAll(/contenteditable=".*?"/g, "")
    .replaceAll(/data-[^=]+=".*?"/g, "")
    // Quill 2 saves every space as &nbsp;, which stops text wrapping between words.
    .replaceAll(/&nbsp;(?!&nbsp;|<)/g, " ");
};

export function printElement(element: HTMLElement, options: { title?: string } = {}) {
  if (!element) return;
  const clone = element.cloneNode(true);

  const styleSheets = Array.from(document.styleSheets)
    .map((styleSheet) => {
      try {
        return Array.from(styleSheet.cssRules)
          .map((rule) => rule.cssText)
          .join("");
      } catch {
        return "";
      }
    })
    .join("\n");

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${(options as { title?: string })?.title || "Print Preview"}</title>
        <style>${styleSheets}</style>
        <style>
          @media print {
            body { margin: 0; padding: 0; }
            @page { margin: 0.5in; }
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background: #fff;
          }
        </style>
      </head>
      <body>
        <div id="print-root"></div>
      </body>
    </html>
  `);
  printWindow.document.close();

  printWindow.onload = () => {
    const printRoot = printWindow.document.getElementById("print-root");
    if (printRoot) {
      printRoot.appendChild(clone);
    } else {
      console.error("print-root element not found in print window.");
      printWindow.close();
      return;
    }

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 200);
  };
}

export type TimeRange =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annually"
  | "custom"
  | "all-time";

export function formatPercentDisplay(value: number | null | undefined, fallback = "-"): string {
  if (value == null || (typeof value === "number" && Number.isNaN(value))) return fallback;
  return numeral(value).format("0.[00]%");
}

export function formatUserName(email: string): string {
  if (!email || email === "unknown") return "Unknown";

  const username = email.split("@")[0];

  let formatted = username
    .replace(/\./g, " ")
    .replace(/\+/g, " ")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");

  return formatted;
}

export function getDateRange(range: TimeRange): { date_from: string; date_to: string } | null {
  const today = dayjs().endOf("day");
  let date_from: dayjs.Dayjs;

  switch (range) {
    case "daily":
      date_from = dayjs().startOf("day");
      break;
    case "weekly":
      date_from = dayjs().subtract(7, "days").startOf("day");
      break;
    case "monthly":
      date_from = dayjs().subtract(30, "days").startOf("day");
      break;
    case "quarterly":
      date_from = dayjs().subtract(90, "days").startOf("day");
      break;
    case "annually":
      date_from = dayjs().subtract(365, "days").startOf("day");
      break;
    case "custom":
      return null;
    default:
      date_from = dayjs().startOf("day");
  }

  return {
    date_from: date_from.toISOString(),
    date_to: today.toISOString(),
  };
}

export function getTimeRangeLabel(range: TimeRange): string {
  switch (range) {
    case "daily":
      return "Daily";
    case "weekly":
      return "Weekly";
    case "monthly":
      return "Monthly";
    case "quarterly":
      return "Quarterly";
    case "annually":
      return "Annually";
    case "custom":
      return "Custom Range";
    case "all-time":
      return "All Time";
    default:
      return "Daily";
  }
}

export const prepareElementForCanvas = (element: HTMLElement): HTMLElement => {
  const clone = element.cloneNode(true) as HTMLElement;

  // Guarantee the root is opaque white so html2canvas has a solid backdrop.
  if (!clone.style.backgroundColor) clone.style.backgroundColor = "#ffffff";
  if (!clone.style.color) clone.style.color = "#000000";

  // For descendants, only fix color (so text is visible) and borderColor
  // where not set. Do NOT touch backgroundColor — child elements without an
  // explicit background must remain transparent so parent box colours
  // (e.g. the T&C disclaimer's #f9fafb fill) show through correctly.
  const allElements = clone.querySelectorAll("*");
  allElements.forEach((el) => {
    const htmlEl = el as HTMLElement;
    if (!htmlEl.style.color) htmlEl.style.color = "#000000";
    if (!htmlEl.style.borderColor) htmlEl.style.borderColor = "#cccccc";
  });

  return clone;
};

export const htmlToPdf = async (element: HTMLElement): Promise<Blob> => {
  try {
    const preparedElement = prepareElementForCanvas(element);

    const tempContainer = document.createElement("div");
    tempContainer.style.position = "absolute";
    tempContainer.style.left = "-9999px";
    tempContainer.style.top = "-9999px";
    tempContainer.appendChild(preparedElement);
    document.body.appendChild(tempContainer);

    const canvas = await html2canvas(preparedElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      allowTaint: true,
    });

    document.body.removeChild(tempContainer);

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "letter",
    });

    const margin = 5;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const contentHeight = pageHeight - margin * 2;

    if (imgHeight <= contentHeight * 1.4) {
      // Scale down slightly if content overflows by up to 40% — keeps everything on one page
      const scale = Math.min(1, contentHeight / imgHeight);
      const scaledW = imgWidth * scale;
      const scaledH = imgHeight * scale;
      const x = margin + (imgWidth - scaledW) / 2;
      pdf.addImage(imgData, "PNG", x, margin, scaledW, scaledH);
    } else {
      // True multi-page for significantly larger content
      const totalPages = Math.ceil(imgHeight / contentHeight);
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, margin - page * contentHeight, imgWidth, imgHeight);
      }
    }

    return pdf.output("blob");
  } catch (error) {
    console.error("Error converting HTML to PDF:", error);
    throw error;
  }
};

export const mergePdfs = async (receiptBlob: Blob, path: string): Promise<Blob> => {
  try {
    const receiptArrayBuffer = await receiptBlob.arrayBuffer();

    const pdfResponse = await sessionFetch(path);
    if (!pdfResponse.ok) {
      throw new Error("Failed to fetch SPV Terms and Conditions PDF");
    }

    const existingArrayBuffer = await pdfResponse.arrayBuffer();

    const receiptPdf = await PDFDocument.load(receiptArrayBuffer);
    const existingPdf = await PDFDocument.load(existingArrayBuffer);

    const copiedPages = await receiptPdf.copyPages(existingPdf, existingPdf.getPageIndices());

    copiedPages.forEach((page) => {
      receiptPdf.addPage(page);
    });

    const mergedPdfBytes = await receiptPdf.save();
    return new Blob([new Uint8Array(mergedPdfBytes)], {
      type: "application/pdf",
    });
  } catch (error) {
    console.error("Error merging PDFs:", error);
    throw error;
  }
};

export const canPreview = (file: File) => {
  if (!file.type) return false;
  return file.type.startsWith("image/") || file.type === "application/pdf";
};

export function formatSizeInMB(bytes: number): string {
  if (!bytes || bytes === 0) return "0 MB";
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export const formatTime = (timestamp: Date): string => {
  const now = dayjs();
  const time = dayjs(timestamp);
  const diffInMinutes = now.diff(time, "minute");
  const diffInHours = now.diff(time, "hour");
  const diffInDays = now.diff(time, "day");
  const diffInWeeks = now.diff(time, "week");
  const diffInMonths = now.diff(time, "month");
  const diffInYears = now.diff(time, "year");

  if (diffInMinutes < 1) {
    return "now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h`;
  } else if (diffInDays < 7) {
    return `${diffInDays}d`;
  } else if (diffInWeeks < 4) {
    return `${diffInWeeks}w`;
  } else if (diffInMonths < 12) {
    return `${diffInMonths}mo`;
  } else {
    return `${diffInYears}y`;
  }
};

export function exportToCsv<T extends Record<string, any>>(filename: string, rows: T[]) {
  if (!rows.length) return;

  const headers = Object.keys(rows[0]);

  const csvContent = [
    headers.map((header) => escapeCSVValue(header)).join(","),
    ...rows.map((row) => headers.map((field) => escapeCSVValue(row[field])).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

export type SpreadsheetExportColumn<T extends Record<string, unknown> = Record<string, unknown>> = {
  key: keyof T & string;
  label: string;
};

export type SpreadsheetExportSummaryRow = {
  label: string;
  value: string | number | boolean | null | undefined;
};

/** Download rows as an Excel-compatible sheet (.xls) with bold header row. */
export function exportToExcelSheet<T extends Record<string, unknown>>(
  filename: string,
  rows: T[],
  columns?: SpreadsheetExportColumn<T>[],
  summary?: SpreadsheetExportSummaryRow[],
) {
  if (!rows.length) return;

  const cols: SpreadsheetExportColumn<T>[] =
    columns ??
    (Object.keys(rows[0]) as (keyof T & string)[]).map((key) => ({
      key,
      label: key,
    }));

  const escapeHtml = (value: unknown) => {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  };

  const summaryTable =
    summary && summary.length > 0
      ? `<table border="1"><tbody>${summary
          .map(
            (row) =>
              `<tr><td><b>${escapeHtml(row.label)}</b></td><td>${escapeHtml(row.value)}</td></tr>`,
          )
          .join("")}</tbody></table><br/>`
      : "";

  const headerRow = cols.map((col) => `<th><b>${escapeHtml(col.label)}</b></th>`).join("");
  const bodyRows = rows
    .map((row) => `<tr>${cols.map((col) => `<td>${escapeHtml(row[col.key])}</td>`).join("")}</tr>`)
    .join("");

  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="utf-8"></head>
<body>
${summaryTable}<table border="1">
<thead><tr>${headerRow}</tr></thead>
<tbody>${bodyRows}</tbody>
</table>
</body>
</html>`;

  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename.endsWith(".xls") ? filename : `${filename}.xls`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generates a consistent color from a string
 * Uses a hash function to convert the string to a number, then maps it to HSL color space
 *
 * @param str - The string to generate a color from
 * @returns An object with background and text colors in HSL format
 */
export function stringToColor(str: string): { bg: string; text: string } {
  if (!str) return { bg: "hsl(0, 0%, 90%)", text: "hsl(0, 0%, 20%)" };

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }

  // Map hash to hue (0-360)
  const hue = Math.abs(hash % 360);

  // Use moderate saturation and lightness for good readability
  const saturation = 65; // 65% saturation for vibrant but not overwhelming colors
  const lightness = 85; // 85% lightness for background (light, pastel-like)

  const bg = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  const text = `hsl(${hue}, ${saturation}%, 30%)`; // Darker version for text

  return { bg, text };
}

export const formatDateForInput = (dateString?: string | null) => {
  if (!dateString) return "";
  return dateString.split("T")[0]; // Returns 'YYYY-MM-DD'
};

export const toPercentFormValue = (value: number | null | undefined): string | number => {
  if (value === null || value === undefined) return "";
  const parsedValue = Number((value * 100).toFixed(2));
  return parsedValue;
};

export const fromPercentFormValue = (value: number | null | undefined): string | number => {
  if (value === null || value === undefined) return "";
  const parsedValue = Number(value / 100);
  return parsedValue;
};

export function getDateLabel(createdAt: string): string {
  const date = new Date(createdAt);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayDate = new Date(
    yesterday.getFullYear(),
    yesterday.getMonth(),
    yesterday.getDate(),
  );
  if (dateDate.getTime() === todayDate.getTime()) return "Today";
  if (dateDate.getTime() === yesterdayDate.getTime()) return "Yesterday";
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function getInitials(name?: string | null): string {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/** Strip non-breaking spaces that break wrapping in strict mail clients (e.g. iCloud). */
export function cleanEmailTemplateText(value: string): string {
  return value.replaceAll("\u00A0", " ").replaceAll("&nbsp;", " ");
}

/**
 * Plain-text fallback for a rich-text HTML body (e.g. Quill output). Inserts
 * newlines at block boundaries before stripping tags \u2014 a plain tag-strip
 * collapses them (`<p>Hi</p><p>Bye</p>` \u2192 "HiBye"), which would run
 * paragraphs together. Strips tags and decodes entities with string
 * replacement rather than `DOMParser`, so untrusted text is never
 * reinterpreted as HTML. Tag-stripping loops to a fixed point \u2014 a single
 * pass can be defeated by nested tags (e.g. `<scr<script>ipt>`) where
 * removing the inner tag reconstructs an outer one.
 */
export function htmlToPlainText(html: string): string {
  const withBreaks = html
    .replaceAll(/<\/(p|div|li|h[1-6]|blockquote)>/gi, "\n")
    .replaceAll(/<br\s*\/?>/gi, "\n");

  let stripped = withBreaks;
  let previous: string;
  do {
    previous = stripped;
    stripped = previous.replaceAll(/<[^>]*>/g, "");
  } while (stripped !== previous);

  return stripped
    .replaceAll("&nbsp;", " ")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&quot;", '"')
    .replaceAll("&#39;", "'")
    .replaceAll("&amp;", "&")
    .replaceAll(/\n{3,}/g, "\n\n")
    .trim();
}

/** Distinct error messages from the rejected results of a Promise.allSettled call. */
export function getSettledRejectionMessages(results: PromiseSettledResult<unknown>[]): string[] {
  const messages = results
    .filter((r): r is PromiseRejectedResult => r.status === "rejected")
    .map((r) => (r.reason instanceof Error ? r.reason.message : String(r.reason)));
  return [...new Set(messages)];
}

/** A copy of `source` holding only `keys`. */
export function pickKeys<T extends object, K extends keyof T>(
  source: T,
  keys: readonly K[],
): Pick<T, K> {
  return Object.fromEntries(keys.map((key) => [key, source[key]])) as Pick<T, K>;
}
